import React, { useState, useRef, useEffect } from 'react';
import { Info, X, ChevronDown, ChevronUp, Search, CircleCheck } from 'lucide-react';
import { useHistory, useLocation } from "react-router-dom";
import Tooltip from '../addentities/Tooltip.js';
import apiHelper from '../../apiHelper';
import CharacterLimitedTextarea from '../../components/CharacterLimitedTextarea';
import DiscardConfirmationModal from '../../components/DiscardConfirmationModal.js';
import UnsavedChangesPrompt from '../../components/UnsavedChangesPrompt.js';
import AlertNotification from '../../components/AlertNotification.js';

// Animated Arrow Icon Component for Parent Category dropdown
const AnimatedArrowIcon = ({ isOpen }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width="18" 
    height="18" 
    viewBox="0 0 18 18" 
    fill="none"
    style={{
      transition: 'transform 0.3s ease',
      transform: isOpen ? 'rotate(0deg)' : 'rotate(180deg)'
    }}
  >
    <path 
      d="M4.5 11.25L9 6.75L13.5 11.25" 
      stroke="#212529" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </svg>
);

export default function AddCategory() {
    const history = useHistory();
    const location = useLocation();
    
    // Initialize state from sessionStorage to persist on refresh
    const getInitialFormData = () => {
        const saved = sessionStorage.getItem('addCategory_formData');
        return saved ? JSON.parse(saved) : {
            category_name: '',
            status: '1',
            parent_category: '',
            prefix: '',
            external_id: '',
            notes: ''
        };
    };

    const getInitialCategories = () => {
        const saved = sessionStorage.getItem('addCategory_categories');
        return saved ? JSON.parse(saved) : [];
    };

    const getInitialParentCategoryState = () => {
        const saved = sessionStorage.getItem('addCategory_parentCategory');
        return saved ? JSON.parse(saved) : {
            search: '',
            selected: null
        };
    };

    const [categories, setCategories] = useState(getInitialCategories);
    const [validationError, setValidationError] = useState([]);
    const [successMessage, setSuccessMessage] = useState('');
    const [disabled, setDisabled] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState(getInitialFormData);
    
    const [errors, setFormErrors] = useState({
        category_name: '',
        status: '',
        external_id: '',
        prefix: ''
    });
   
    // Track field touched state for onBlur validation
    const [touchedFields, setTouchedFields] = useState({
        category_name: false,
        status: false,
        external_id: false,
        prefix: false
    });
    
    // Track typing state for ID and prefix fields to clear errors when user starts typing
    const [isTypingId, setIsTypingId] = useState(false);
    const [isTypingPrefix, setIsTypingPrefix] = useState(false);
    
    // Alert state for toast notifications
    const [alert, setAlert] = useState(null);
    const [showItemAlert, setShowItemAlert] = useState(false);
    
    // Parent category dropdown state
    const [isParentCategoryOpen, setIsParentCategoryOpen] = useState(false);
    const [parentCategoryState, setParentCategoryState] = useState(getInitialParentCategoryState);
    
    const parentCategorySearch = parentCategoryState.search;
    const selectedParentCategory = parentCategoryState.selected;

    // Save state to sessionStorage whenever it changes
    useEffect(() => {
        sessionStorage.setItem('addCategory_formData', JSON.stringify(formData));
    }, [formData]);

    useEffect(() => {
        sessionStorage.setItem('addCategory_categories', JSON.stringify(categories));
    }, [categories]);

    useEffect(() => {
        sessionStorage.setItem('addCategory_parentCategory', JSON.stringify(parentCategoryState));
    }, [parentCategoryState]);

    // Clear saved state when component unmounts or when successfully saved
    useEffect(() => {
        return () => {
            // Only clear if we're not in the middle of a save operation
            if (!isSaving) {
                sessionStorage.removeItem('addCategory_formData');
                sessionStorage.removeItem('addCategory_categories');
                sessionStorage.removeItem('addCategory_parentCategory');
            }
        };
    }, [isSaving]);

    // Alert notification handler
    const showAlert = (type, action, recordName) => {
        setAlert({
            type,        // success | error | info
            action,      // create | update
            recordName,  // e.g. "Category"
        });
        setShowItemAlert(true);
        setTimeout(() => setShowItemAlert(false), 5000);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
       
        // Set typing state for ID and prefix fields
        if (name === 'external_id') {
            setIsTypingId(true);
        } else if (name === 'prefix') {
            setIsTypingPrefix(true);
        }
        // Clear error when user starts typing in a field with error
        if (errors[name]) {
            setFormErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
        // Real-time validation for required fields when user clears them
        if (touchedFields[name] && !value && name !== 'external_id' && name !== 'prefix') {
            setFormErrors(prev => ({
                ...prev,
                [name]: `${getFieldDisplayName(name)} is required`
            }));
        } else if (touchedFields[name] && value) {
            // Clear error if field is filled
            setFormErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    // Helper function to get display names for fields
    const getFieldDisplayName = (fieldName) => {
        const fieldNames = {
            category_name: 'Category Name',
            status: 'Status',
            external_id: 'ID',
            prefix: 'Prefix'
        };
        return fieldNames[fieldName] || fieldName;
    };

    // Handle field blur for validation
    const handleBlur = async (e) => {
        const { name, value } = e.target;
       
        // Reset typing state
        if (name === 'external_id') {
            setIsTypingId(false);
        } else if (name === 'prefix') {
            setIsTypingPrefix(false);
        }
       
        // Mark field as touched
        setTouchedFields(prev => ({
            ...prev,
            [name]: true
        }));
        
        // Required field validation (skip ID and prefix)
        if (!value && name !== 'external_id' && name !== 'prefix') {
            setFormErrors(prev => ({
                ...prev,
                [name]: `${getFieldDisplayName(name)} is required`
            }));
            return;
        }
        
        // Duplicate check for category_name, id, and prefix on blur
        if (name === 'category_name' && value) {
            await checkDuplicate(name, value);
        } else if (name === 'external_id' && value && value.trim() !== '') {
            await checkDuplicate(name, value);
        } else if (name === 'prefix' && value && value.trim() !== '') {
            await checkDuplicate(name, value);
        }
    };

    // Function to check for duplicates
    const checkDuplicate = async (fieldName, value) => {
        try {
            // Skip duplicate check for empty IDs and prefixes
            if ((fieldName === 'external_id' || fieldName === 'prefix') && (!value || value.trim() === '')) {
                setFormErrors(prev => ({
                    ...prev,
                    [fieldName]: ''
                }));
                return;
            }
           
            // Check if category with same name, ID or prefix already exists
            const existingCategory = categories.find(cat => {
                if (fieldName === 'external_id' || fieldName === 'prefix') {
                    // Only check against categories that have non-empty values
                    return cat[fieldName] &&
                           cat[fieldName].trim() !== '' &&
                           cat[fieldName].toLowerCase() === value.toLowerCase();
                } else {
                    return cat[fieldName]?.toLowerCase() === value.toLowerCase();
                }
            });
           
            if (existingCategory) {
                setFormErrors(prev => ({
                    ...prev,
                    [fieldName]: `${getFieldDisplayName(fieldName)} already exists`
                }));
            } else {
                // Clear duplicate error if no duplicate found
                setFormErrors(prev => ({
                    ...prev,
                    [fieldName]: prev[fieldName]?.includes('already exists') ? '' : prev[fieldName]
                }));
            }
        } catch (error) {
            console.error('Error checking duplicate:', error);
        }
    };

    useEffect(() => {
        async function fetchData() {
            try {
                const response = await apiHelper('inventory/categories/?is_archived=false');
                if (response && response.ok) {
                    const result = await response.json();
                    setCategories(result);
                    console.log('Categories loaded:', result);
                }
            } catch (error) {
                console.error('Error fetching categories:', error);
            }
        }
        
        // Only fetch if we don't have categories in sessionStorage
        if (categories.length === 0) {
            fetchData();
        }
    }, []);

    const [activeTab] = useState("Add Category");
    const breadcrumbs = ["Product", "Category", activeTab];

    const [discardModalOpen, setDiscardModalOpen] = useState(false);
    const discardRef = useRef(null);

    const handleSave = async () => {
        // Set saving state to true to prevent discard modal from showing
        setIsSaving(true);
        setDisabled(true);
       
        // Mark all required fields as touched to show all errors (skip ID and prefix)
        setTouchedFields({
            category_name: true,
            status: true,
            external_id: true,
            prefix: true
        });

        let form_errors = { category_name: '', status: '', external_id: '', prefix: '' };
        let is_error = false;
        
        // Required field validation (skip ID and prefix)
        if (!formData.category_name) {
            form_errors["category_name"] = "Category Name is required";
            is_error = true;
        }
        if (!formData.status) {
            form_errors["status"] = "Status is required";
            is_error = true;
        }
        
        // ID character limit validation
        if (formData.external_id && formData.external_id.length > 50) {
            is_error = true;
        }
        
        // Prefix character limit validation
        if (formData.prefix && formData.prefix.length > 50) {
            form_errors["prefix"] = "Prefix cannot exceed 50 characters";
            is_error = true;
        }
        
        // ID and prefix are optional, so no required validation
        // Duplicate validation before API call
        if (formData.category_name) {
            const duplicateName = categories.find(cat =>
                cat.category_name?.toLowerCase() === formData.category_name.toLowerCase()
            );
            if (duplicateName) {
                form_errors["category_name"] = "Category Name already exists";
                is_error = true;
            }
        }
        
        // Only check for duplicate ID if ID is provided and not empty
        if (formData.external_id && formData.external_id.trim() !== '' && formData.external_id.length <= 50) {
            const duplicateId = categories.find(cat =>
                cat.external_id &&
                cat.external_id.trim() !== '' &&
                cat.external_id.toLowerCase() === formData.external_id.toLowerCase()
            );
            if (duplicateId) {
                form_errors["external_id"] = "Category ID already exists";
                is_error = true;
            }
        }
        
        // Only check for duplicate prefix if prefix is provided and not empty
        if (formData.prefix && formData.prefix.trim() !== '' && formData.prefix.length <= 50) {
            const duplicatePrefix = categories.find(cat =>
                cat.prefix &&
                cat.prefix.trim() !== '' &&
                cat.prefix.toLowerCase() === formData.prefix.toLowerCase()
            );
            if (duplicatePrefix) {
                form_errors["prefix"] = "Prefix already exists";
                is_error = true;
            }
        }
        
        setFormErrors(form_errors);
       
        if (is_error) {
            setDisabled(false);
            setIsSaving(false);
            showAlert("error", "create", "Category");
            return false;
        }

        try {
            const response = await apiHelper('inventory/categories/create/', 'POST', formData);
           
            if (response && response.ok) {
                setSuccessMessage('Category saved successfully.');
                setValidationError([]);
                setFormErrors({ category_name: '', status: '', external_id: '', prefix: '' });
                setTouchedFields({ category_name: false, status: false, external_id: false, prefix: false });
               
                // Clear saved state before redirecting
                sessionStorage.removeItem('addCategory_formData');
                sessionStorage.removeItem('addCategory_categories');
                sessionStorage.removeItem('addCategory_parentCategory');
               
                // Reset form
                setFormData({
                    category_name: '',
                    status: '1',
                    notes: '',
                    parent_category: '',
                    prefix: '',
                    external_id: ''
                });
               
                // Reset parent category state
                setParentCategoryState({ search: '', selected: null });
               
                // Reset dirty and saving states
                setIsDirty(false);
                setIsSaving(false);
                
                // Show success alert
                showAlert("success", "create", "Category");
                
                // Redirect to category list
                // setTimeout(() => {
                //     history.push("/category");
                // }, 1000);
                history.push("/category", { success: "Category Saved Successfully" });
            return;
               
            } else {
                setDisabled(false);
                setIsSaving(false);
                if (response) {
                    const errorData = await response.json();
                    setValidationError(errorData.errors || ['Error saving category']);
                    
                    // Show error alert
                    showAlert("error", "create", "Category");
                } else {
                    setValidationError(['Network error occurred']);
                    
                    // Show error alert
                    showAlert("error", "create", "Category");
                }
            }
        } catch (error) {
            setDisabled(false);
            setIsSaving(false);
            setValidationError(['Network error occurred']);
            console.error('Save error:', error);
            
            // Show error alert
            showAlert("error", "create", "Category");
        }
    };

    const handleDiscard = () => {
        // If no changes or we're in the middle of saving, redirect without confirmation
        if (!isDirty || isSaving) {
            // Clear saved state before redirecting
            sessionStorage.removeItem('addCategory_formData');
            sessionStorage.removeItem('addCategory_categories');
            sessionStorage.removeItem('addCategory_parentCategory');
            
            history.push("/category");
        } else {
            // Show discard confirmation if there are unsaved changes and we're not saving
            setDiscardModalOpen(true);
        }
    };

    const handleDiscardConfirm = (confirm) => {
    if (confirm) {
        // Clear saved state
        sessionStorage.removeItem('addCategory_formData');
        sessionStorage.removeItem('addCategory_categories');
        sessionStorage.removeItem('addCategory_parentCategory');
        
        // Disable the unsaved changes prompt before redirecting
        setIsDirty(false);
       
        // Redirect to category list with success state
        history.push("/category", { 
            success: "Category Discarded Successfully",
            action: "discard",
            refresh: true
        });
    }
    setDiscardModalOpen(false);
};

    const handleCancelDiscard = () => {
        setDiscardModalOpen(false);
    };

    // Custom block function for UnsavedChangesPrompt
    const handleBlockNavigation = (location, action) => {
        // Don't block if we're saving or if there are no unsaved changes
        if (isSaving || !isDirty) {
            return false;
        }
       
        // Show discard modal for navigation attempts
        setDiscardModalOpen(true);
        return true; // Block navigation
    };

    const parentCategoryRef = useRef(null);
    const dropdownRef = useRef(null);

    const toggleParentCategory = () => {
        setIsParentCategoryOpen((prev) => !prev);
    };

    // Auto-open dropdown when user starts typing
    const handleParentCategoryInputChange = (e) => {
        const value = e.target.value;
        setParentCategoryState({ search: value, selected: null });
        setFormData({ ...formData, parent_category: '' });
        setIsDirty(true);
        
        // Automatically open dropdown when user types something
        if (value.trim() && !isParentCategoryOpen) {
            setIsParentCategoryOpen(true);
        }
    };

    // Validate parent category when user clicks outside
    const validateParentCategory = () => {
        // If there's a search value but no selected category, check if it matches any available option
        if (parentCategorySearch.trim() && !selectedParentCategory) {
            const matchingCategory = categories.find(cat => 
                cat.category_name.toLowerCase() === parentCategorySearch.toLowerCase()
            );
            
            // If no matching category found, clear the field
            if (!matchingCategory) {
                setParentCategoryState({ search: '', selected: null });
                setFormData({ ...formData, parent_category: '' });
            } else {
                // If matching category found, select it
                setParentCategoryState({ search: '', selected: matchingCategory });
                setFormData({ ...formData, parent_category: matchingCategory.category_id });
            }
        }
    };

    useEffect(() => {
        const handleClickOutside = (e) => {
            // Check if click is outside both the parent category container AND the dropdown
            if (isParentCategoryOpen && 
                parentCategoryRef.current && 
                !parentCategoryRef.current.contains(e.target) &&
                dropdownRef.current && 
                !dropdownRef.current.contains(e.target)) {
                
                // Validate parent category before closing dropdown
                validateParentCategory();
                setIsParentCategoryOpen(false);
            }
        };
        
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isParentCategoryOpen, parentCategorySearch, selectedParentCategory, categories]);

    // Clear parent category search and selection
    const clearParentCategory = (e) => {
        e.stopPropagation();
        setParentCategoryState({ search: '', selected: null });
        setFormData({ ...formData, parent_category: '' });
        setIsParentCategoryOpen(false);
        setIsDirty(true);
    };

    const filteredCategories = categories.filter((item) =>
        item.category_name.toLowerCase().includes(parentCategorySearch.toLowerCase())
    );

    const handleCategorySelect = (category) => {
        setParentCategoryState({ search: '', selected: category });
        setFormData({ ...formData, parent_category: category.category_id });
        setIsParentCategoryOpen(false);
        setIsDirty(true);
    };

    // Handle blur event for parent category input
    const handleParentCategoryBlur = (e) => {
        // Use setTimeout to ensure this runs after the click outside handler
        setTimeout(() => {
            validateParentCategory();
        }, 100);
    };

    const saveRef = useRef(null);
    const [saveModalOpen, setSaveModalOpen] = useState(false);

    const handleSaveConfirm = () => {
        setSaveModalOpen(false);
    };

    useEffect(() => {
        function handleClickOutside(event) {
            if (saveRef.current && !saveRef.current.contains(event.target)) {
                setSaveModalOpen(false);
            }
        }
        if (saveModalOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [saveModalOpen]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (discardRef.current && !discardRef.current.contains(event.target)) {
                setDiscardModalOpen(false);
            }
        }
        if (discardModalOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [discardModalOpen]);

    const handleChangeWithDirty = (e) => {
        if (!isDirty) {
            setIsDirty(true);
        }
        handleChange(e);
    };

    // Combined change handler that includes blur for validation
    const handleChangeWithValidation = (e) => {
        handleChangeWithDirty(e);
    };

    return (
        <>
            <div className="company-body main-company-body-inner new-look" style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                overflow: 'hidden',
            }}>
                {/* Alert Notification */}
                {showItemAlert && alert && (
                    <AlertNotification
                        type={alert.type}
                        action={alert.action}
                        recordName={alert.recordName}
                        onClose={() => setShowItemAlert(false)}
                    />
                )}
                
                <div className="breadcrumb">
                    {breadcrumbs.map((crumb, index) => {
                        const isLast = index === breadcrumbs.length - 1;
                        return (
                            <span
                                key={index}
                                className={`breadcrumb-item ${crumb === "Category" ? "clickable" : ""}`}
                                onClick={() => {
                                    if (crumb === "Category") {
                                        if (!isDirty || isSaving) {
                                            history.push("/category");
                                        } else {
                                            setDiscardModalOpen(true);
                                        }
                                    }
                                }}
                                style={{ cursor: crumb === "Category" ? "pointer" : "default" }}
                            >
                                {crumb}
                                {!isLast ? " " : ""}
                            </span>
                        );
                    })}
                </div>
                <div className='edit-organisation-info'>
                    <div className='edit-organisation-left'>
                        <h1>Add Category</h1>
                    </div>
                    <div className='discard-save-organisation-right d-flex align-items-center gap-2'>
                        <button
                            className="dark-button"
                            type='button'
                            onClick={handleDiscard}
                            style={{width:'100px',padding:'8px',height:"34px"}}
                        >
                            Discard
                        </button>
                         <DiscardConfirmationModal
                            isOpen={discardModalOpen}
                            onCancel={handleCancelDiscard}
                            onConfirm={handleDiscardConfirm}
                            entityName="Category"
                        />
                        <button
                            className="dark-border"
                            type='button'
                            onClick={handleSave}
                            disabled={disabled}
                            style={{width:'100px',padding:'8px',height:"34px"}}
                        >
                            Save
                        </button>
                    </div>
                </div>
                <div style={{
                    flex: 1,
                    overflowY: 'auto',
                    paddingBottom: '24px',
                }}>
                    <div className="info-section">
                        <div className="info-card new-info-overflow-auto">
                            <div className="info-header">
                                <h4 className="info-title">Basic Info</h4>
                            </div>
                            <div className="info-content d-block">
                                <div className='grid-info-adding'>
                                    <div className='row gappingcls-added'>
                                        <div className='col-lg-6 col-md-6 col-12'>
                                            <div className="info-inner">
                                                <label
                                                    htmlFor="categoryName"
                                                    className="form-label d-flex align-items-center gap-1"
                                                >
                                                    Category Name <span className='error-color'>*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    className={`form-control common-input-all ${errors.category_name ? 'err-color' : ''}`}
                                                    id="categoryName"
                                                    name="category_name"
                                                    value={formData.category_name}
                                                    onChange={handleChangeWithValidation}
                                                    onBlur={handleBlur}
                                                />
                                                {errors.category_name && <p style={{ color: '#DC3545', fontSize: '12px', marginTop: '4px', marginBottom: '0' }}>{errors.category_name}</p>}
                                            </div>
                                        </div>
                                        <div className='col-lg-6 col-md-6 col-12'>
                                            <div className="info-inner">
                                                <label htmlFor="status" className="form-label">
                                                    Status <span className='error-color'>*</span>
                                                </label>
                                                <div className='flexing-type-radio'>
                                                    <div className="form-check">
                                                        <input
                                                            className={`form-check-input ${errors.status ? 'err-color' : ''}`}
                                                            type="radio"
                                                            id="flexRadioDefault1"
                                                            name="status"
                                                            value="1"
                                                            checked={formData.status === '1'}
                                                            onChange={handleChangeWithValidation}
                                                            onBlur={handleBlur}
                                                        />
                                                        <label
                                                            className="form-check-label"
                                                            htmlFor="flexRadioDefault1"
                                                            style={{ fontWeight: 'normal', fontSize: '14px' }}
                                                        >
                                                            Active
                                                        </label>
                                                    </div>
                                                    <div className="form-check">
                                                        <input
                                                            className={`form-check-input ${errors.status ? 'error-radio' : ''}`}
                                                            type="radio"
                                                            id="flexRadioDefault2"
                                                            name="status"
                                                            value="0"
                                                            checked={formData.status === '0'}
                                                            onChange={handleChangeWithValidation}
                                                            onBlur={handleBlur}
                                                        />
                                                        <label
                                                            className="form-check-label"
                                                            htmlFor="flexRadioDefault2"
                                                            style={{ fontWeight: 'normal', fontSize: '14px' }}
                                                        >
                                                            Inactive
                                                        </label>
                                                    </div>
                                                </div>
                                                {errors.status && <p style={{ color: '#DC3545', fontSize: '12px', marginTop: '4px', marginBottom: '0' }}>{errors.status}</p>}
                                            </div>
                                        </div>
                                    </div>
                                    <div className='row gappingcls-added'>
                                        <div className='col-lg-6 col-md-6 col-12'>
                                            <div className="info-inner">
                                                <label
                                                    htmlFor="categoryId"
                                                    className="form-label d-flex align-items-center gap-2"
                                                >
                                                    ID
                                                </label>
                                                <CharacterLimitedTextarea
                                                    id="categoryId"
                                                    name="external_id"
                                                    value={formData.external_id}
                                                    onChange={handleChangeWithValidation}
                                                    onBlur={handleBlur}
                                                    maxLength={50}
                                                    counterPosition="end"
                                                    className={errors.external_id && !isTypingId ? 'err-color' : ''}
                                                    error={errors.external_id && !isTypingId ? errors.external_id : ''}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="info-card new-info-overflow-auto">
                            <div className="info-header">
                                <h4 className="info-title">Business Info</h4>
                            </div>
                            <div className="info-content d-block">
                                <div className='grid-info-adding'>
                                    <div className='row gappingcls-added'>
                                        <div className='col-lg-6 col-md-6 col-12'>
                                            <div
                                                className="info-inner"
                                                style={{ position: 'relative' }}
                                                ref={parentCategoryRef}
                                            >
                                                <label className="form-label d-flex justify-between align-items-center gap-2">
                                                    Parent Category
                                                    <Tooltip
                                                        title="Parent Category"
                                                        body="The main category this category belongs under if any">
                                                        <Info size={16} className="info-icon" />
                                                    </Tooltip>
                                                </label>
                                                <div className="relative" style={{ position: 'relative' }}>
                                                    <div
                                                        className="form-control common-input-all d-flex justify-between align-items-center cursor-pointer rounded p-2"
                                                        onClick={() => setIsParentCategoryOpen(!isParentCategoryOpen)}
                                                        style={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            height: '39px',
                                                            position: 'relative',
                                                            padding: '8px 12px'
                                                        }}
                                                    >
                                                        <input
                                                            type="text"
                                                            style={{
                                                                width: '100%',
                                                                border: 'none',
                                                                outline: 'none',
                                                                background: 'transparent',
                                                                fontSize: '14px',
                                                                cursor: 'pointer',
                                                                padding: 0,
                                                                flex: 1
                                                            }}
                                                            value={selectedParentCategory ? selectedParentCategory.category_name : parentCategorySearch}
                                                            onChange={handleParentCategoryInputChange}
                                                            onFocus={() => setIsParentCategoryOpen(true)}
                                                            onBlur={handleParentCategoryBlur}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                if (!isParentCategoryOpen) {
                                                                    setIsParentCategoryOpen(true);
                                                                }
                                                            }}
                                                        />
                                                        <div className="d-flex align-items-center gap-1" style={{ flexShrink: 0 }}>
                                                            {(selectedParentCategory || parentCategorySearch) && (
                                                                <X
                                                                    size={16}
                                                                    style={{ cursor: 'pointer' }}
                                                                    onClick={clearParentCategory}
                                                                />
                                                            )}
                                                            <AnimatedArrowIcon isOpen={isParentCategoryOpen} />
                                                        </div>
                                                    </div>
                                                    {isParentCategoryOpen && (
                                                        <div
                                                            ref={dropdownRef}
                                                            className="absolute z-10 w-full bg-white border rounded shadow-lg mt-1"
                                                            style={{
                                                                position: 'absolute',
                                                                top: '100%',
                                                                left: 0,
                                                                right: 0,
                                                                maxHeight: '270px',
                                                                overflowY: 'auto',
                                                                fontFamily: 'Montserrat',
                                                                fontSize: '14px',
                                                                zIndex: 1000,
                                                                backgroundColor: 'white'
                                                            }}
                                                        >
                                                            {filteredCategories.length > 0 ? (
                                                                filteredCategories.map((item) => (
                                                                    <div
                                                                        key={item.category_id}
                                                                        className=" hover:bg-gray-100 cursor-pointer"
                                                                        onClick={() => handleCategorySelect(item)}
                                                                        onMouseDown={(e) => e.preventDefault()} // Prevent blur on mouse down
                                                                        style={{
                                                                            padding: '10px'
                                                                        }}
                                                                    >
                                                                        <div style={{color: '#212529', fontWeight: '400', fontSize: '14px'}}>
                                                                            {item.category_name}
                                                                        </div>
                                                                        {item.parent_name && (
                                                                            <div style={{ fontSize: '12px', color: '#6C757D', marginTop: '2px' }}>
                                                                                {item.parent_parent_name
                                                                                    ? `${item.parent_parent_name} >> ${item.parent_name}`
                                                                                    : item.parent_name}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                ))
                                                            ) : (
                                                                <div className=" text-gray-500" style={{ padding: '10px' }}>
                                                                    {parentCategorySearch ? 'No categories found' : 'No categories available'}
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div className='col-lg-6 col-md-6 col-12'>
                                            <div className="info-inner">
                                                <label
                                                    htmlFor="prefix"
                                                    className="form-label d-flex align-items-center gap-1"
                                                >
                                                    Prefix
                                                    <Tooltip
                                                        title="Prefix"
                                                        body="A short code for the category, used in automating SKU generation">
                                                        <Info size={16} className="info-icon" />
                                                    </Tooltip>
                                                </label>
                                                <input
                                                    type="text"
                                                    className={`form-control common-input-all ${errors.prefix && !isTypingPrefix ? 'err-color' : ''}`}
                                                    id="prefix"
                                                    name="prefix"
                                                    value={formData.prefix}
                                                    onChange={handleChangeWithValidation}
                                                    onBlur={handleBlur}
                                                />
                                                {errors.prefix && !isTypingPrefix && <p style={{ color: '#DC3545', fontSize: '12px', marginTop: '4px', marginBottom: '0' }}>{errors.prefix}</p>}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="info-card">
                            <div className="info-header">
                                <h4 className="info-title">Notes</h4>
                            </div>
                            <div className="info-content">
                                <div className="info-inner common-textarea">
                                    <textarea
                                        className="form-control w-100 common-textarea-all"
                                        id="exampleFormControlTextarea1"
                                        rows="3"
                                        name="notes"
                                        value={formData.notes}
                                        onChange={handleChangeWithDirty}
                                    ></textarea>
                                </div>
                            </div>
                        </div>
                        {/* Only show UnsavedChangesPrompt when discard modal is NOT open */}
                        {!discardModalOpen && (
                            <UnsavedChangesPrompt
                                isDirty={isDirty && !isSaving}
                                when={isDirty && !isSaving}
                                entityName={'Category'}
                                navigate={handleBlockNavigation}
                            />
                        )}
                    </div>
                    {saveModalOpen && (
                        <>
                            <div className="drawer-overlay" />
                            <div className="entity-drawer modal-popup-cls" ref={saveRef}
                                style={{
                                    maxWidth: 650,
                                    margin: "0 auto",
                                    background: "white",
                                    borderRadius: 12,
                                    zIndex: 1050,
                                    padding: 24,
                                    position: "fixed",
                                    top: "50%",
                                    left: "50%",
                                    transform: "translate(-50%, -50%)",
                                    boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.2)",
                                    height: "auto",
                                    right: "auto",
                                    overflow: "inherit",
                                }}>
                                <div className="drawer-header d-flex justify-content-end align-items-center pt-0 pe-0">
                                    <button className="close-btn" onClick={() => setSaveModalOpen(false)}>
                                        <X size={20} />
                                    </button>
                                </div>
                                <h3 style={{ textAlign: "left", color: "#212529", fontSize: "20px", fontWeight: "600", lineHeight: "24px", fontFamily: "Montserrat", marginBottom: "5px" }}>New feature in Progress</h3>
                                <p style={{ marginBottom: "0", textAlign: "left", color: "#6C757D", fontSize: "14px", fontWeight: "400", lineHeight: "20px", fontFamily: "Montserrat" }}>Work in Progress</p>
                                <div className="drawer-footer" style={{ paddingTop: "10px" }}>
                                    <button className="btn-save" onClick={handleSaveConfirm}>
                                        OK
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </>
    );
}